import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";

// Détection d'une nouvelle version du site (web / PWA) : le manifeste des fichiers construits
// (asset-manifest.json) nomme le script principal avec son empreinte (main.<hash>.js). Si celui
// servi par le serveur n'est plus celui chargé dans la page → une version plus récente est déployée.
// App native : le web est embarqué dans l'app (mise à jour par les stores) → jamais de vérification.

export const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Script principal actuellement chargé (chemin absolu) — null si introuvable (ex. tests).
export function loadedMainScript(doc = document) {
  const el = [...doc.querySelectorAll("script[src]")].find((s) => /\/static\/js\/(main|bundle)[^/]*\.js/.test(s.getAttribute("src") || ""));
  if (!el) return null;
  try { return new URL(el.getAttribute("src"), doc.baseURI).pathname; } catch { return null; }
}

// Compare le manifeste servi et le script chargé : true si le déploiement a changé.
export function isNewerBuild(manifest, loaded) {
  const served = manifest?.files?.["main.js"];
  if (!served || !loaded) return false;
  let servedPath = served;
  try { servedPath = new URL(served, "https://x.invalid").pathname; } catch { /* chemin relatif brut */ }
  return servedPath !== loaded;
}

export async function checkForNewBuild(fetchImpl = fetch) {
  if (Capacitor.isNativePlatform()) return false;
  const loaded = loadedMainScript();
  if (!loaded) return false;
  try {
    const res = await fetchImpl(`/asset-manifest.json?t=${Date.now()}`, { cache: "no-store", credentials: "omit" });
    if (!res.ok) return false;
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("json")) return false; // hébergeur qui renvoie index.html pour les fichiers absents
    return isNewerBuild(await res.json(), loaded);
  } catch { return false; }
}

// --- App native : version « store » déclarée en Admin (settings.app_release, lue via /config/public)
// comparée au binaire installé → bandeau « Nouvelle version disponible » qui ouvre la fiche du store.
export const STORE_UPDATE_SNOOZE_MS = 2 * 24 * 60 * 60 * 1000;
export const STORE_UPDATE_SNOOZE_KEY = "yalola.storeUpdateSnooze";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.yalola";

// {version, build} du store si plus récent que le build installé, sinon null.
export function storeUpdateAvailable(release, platform, installedBuild) {
  const rel = release?.[platform];
  const target = Number(rel?.build);
  const current = Number(installedBuild);
  if (!Number.isFinite(target) || !Number.isFinite(current) || target <= 0 || current <= 0 || target <= current) return null;
  return { version: String(rel.version || ""), build: target };
}

// Fiche du store : URL admin (Paramètres › PWA) sinon Play Store ; iOS sans URL → page /get-app (boutons store).
export function storeUrl(settings, platform) {
  const pwa = settings?.pwa || {};
  if (platform === "android") return pwa.store_android_url || PLAY_STORE_URL;
  if (platform === "ios") return pwa.store_ios_url || "/get-app?update=1";
  return "/get-app?update=1";
}

export function isStoreUpdateSnoozed(build, now = Date.now(), storage = globalThis.localStorage) {
  try {
    const raw = storage?.getItem(STORE_UPDATE_SNOOZE_KEY);
    if (!raw) return false;
    const { until, build: b } = JSON.parse(raw);
    return Number(b) === Number(build) && Number(until) > now;
  } catch { return false; }
}

export function snoozeStoreUpdate(build, now = Date.now(), storage = globalThis.localStorage) {
  try { storage?.setItem(STORE_UPDATE_SNOOZE_KEY, JSON.stringify({ build: Number(build), until: now + STORE_UPDATE_SNOOZE_MS })); } catch { /* stockage indisponible */ }
}

// Hook : {version, build} du store si l'app native installée est plus ancienne, sinon null (web → null).
export function useOutdatedBuild(release) {
  const [update, setUpdate] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!Capacitor.isNativePlatform() || !release) { setUpdate(null); return undefined; }
    import("@/lib/appVersion").then(({ getAppVersion }) => getAppVersion()).then((v) => {
      if (alive) setUpdate(storeUpdateAvailable(release, Capacitor.getPlatform(), v?.build));
    }).catch(() => { if (alive) setUpdate(null); });
    return () => { alive = false; };
  }, [release]);
  return update;
}
